import { ListGroup, Form, Button } from "react-bootstrap";
import ConditionalSpinner from "../../../components/ConditionalSpinner";
import { useGetProjectThumbnailsQuery, useGetTasksQuery } from "../../../store/api/kinesense";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import ReviewVideoListItem from "./ReviewVideoListItem";
import { SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMediaSources from "../../../hooks/useMediaSources";
import {
    faArrowDownShortWide,
    faArrowUpShortWide,
    faCircleExclamation,
    faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { Task } from "../../../models/tasks/Task";

type MediaSourceSortBy = "importTime" | "name" | "startTime" | "endTime";

export interface ReviewVideoListProps {
    viewId: string;
    mediaId: string;
    setMediaId: (_: SetStateAction<string>) => void;
    isExpanded: boolean;
    setIsExpanded: (_: React.SetStateAction<boolean>) => void;
}

export function getReviewVideoListItemId(mediaId: string) {
    return `video-list-${mediaId}`;
}

function ReviewVideoList(props: ReviewVideoListProps) {
    const projectId = useSelector((state: ApplicationState) => state.general.activeProjectId);
    const videoSidebarRef = useRef<HTMLDivElement>(null);
    const listGroupRef = useRef<HTMLDivElement>(null);
    const [searchText, setSearchText] = useState("");
    const [appliedSearchText, setAppliedSearchText] = useState("");
    const [sortBy, setSortBy] = useState<MediaSourceSortBy>("importTime");
    const [isSortAscending, setIsSortAscending] = useState(false);
    const [showFailedImports, setShowFailedImports] = useState(false);

    const { allMediaSources, hasLoadedMediaSourcesInitial } = useMediaSources(props.viewId);
    const {
        data: thumbnails,
        isSuccess,
        isFetching,
        refetch,
    } = useGetProjectThumbnailsQuery({ projectId }, { skip: !projectId });
    const hasLoadedThumbnails = isSuccess && !isFetching;

    const { data: tasks } = useGetTasksQuery({ projectId }, { skip: !projectId });

    const activeImportTaskByMediaId = useMemo(() => {
        const taskByMediaId = new Map<string, Task | undefined>();

        for (const task of tasks ?? []) {
            if (task.type !== "import") {
                continue;
            }

            const mediaId = task.references?.mediaId;
            if (!mediaId) {
                continue;
            }

            if (task.isFailed || task.isComplete) {
                if (!taskByMediaId.has(mediaId)) {
                    taskByMediaId.set(mediaId, undefined);
                }
                continue;
            }

            taskByMediaId.set(mediaId, task);
        }

        return taskByMediaId;
    }, [tasks]);

    function isFailedImport(mediaId: string, displayCount: number | undefined) {
        return (displayCount ?? 0) === 0 && activeImportTaskByMediaId.get(mediaId) === undefined;
    }

    const failedImportsCount = useMemo(() => {
        return (allMediaSources ?? []).reduce((count, mediaSource) => {
            return isFailedImport(mediaSource.mediaId, mediaSource.files?.displayCount) ? count + 1 : count;
        }, 0);
    }, [allMediaSources, activeImportTaskByMediaId]);

    const displayMediaSources = useMemo(() => {
        const search = appliedSearchText.trim().toLowerCase();

        const filtered = (allMediaSources ?? []).filter((mediaSource) => {
            const failedImport = isFailedImport(mediaSource.mediaId, mediaSource.files?.displayCount);
            if (!showFailedImports && failedImport) {
                return false;
            }

            if (!search) {
                return true;
            }

            return mediaSource.name?.toLowerCase().includes(search) ?? false;
        });

        return filtered.sort((a, b) => {
            let result = 0;

            switch (sortBy) {
                case "name":
                    result = (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
                    break;
                case "startTime":
                    result = a.startsAt - b.startsAt;
                    break;
                case "endTime": {
                    const aEndsAt = a.startsAt + a.duration;
                    const bEndsAt = b.startsAt + b.duration;
                    result = aEndsAt - bEndsAt;
                    break;
                }
                case "importTime":
                default:
                    result = a.createdAt - b.createdAt;
                    break;
            }

            return isSortAscending ? result : -result;
        });
    }, [allMediaSources, appliedSearchText, showFailedImports, sortBy, isSortAscending, activeImportTaskByMediaId]);

    // Re-fetch thumbnails before the signed URLs expire
    useEffect(() => {
        if (!thumbnails?.expiresAt) {
            return;
        }

        const now = Date.now();
        const expiresIn = Math.max((thumbnails.expiresAt - now) / 2);
        if (expiresIn <= 0) {
            console.error(`Invalid thumbnail expiration time: ${expiresIn}ms`);
            return;
        }
        const handle = setTimeout(refetch, expiresIn);
        return () => {
            clearTimeout(handle);
        };
    }, [thumbnails?.expiresAt]);

    useEffect(() => {
        const trimmedText = searchText.trim();

        if (trimmedText.length < 3) {
            setAppliedSearchText("");
            return;
        }

        setAppliedSearchText(trimmedText);
    }, [searchText]);

    // Scroll selected media source into view if it is not fully contained within the scrollable view
    useEffect(() => {
        if (!props?.mediaId || !videoSidebarRef.current || !listGroupRef.current || !document?.getElementById) {
            return;
        }

        const top = listGroupRef.current.offsetTop - videoSidebarRef.current.getBoundingClientRect().top;
        const bottom = videoSidebarRef.current.offsetHeight + top;

        // NOTE: requires element ID for each video list item to be set correctly
        const el = document.getElementById(getReviewVideoListItemId(props.mediaId));
        if (!el?.scrollIntoView) {
            return;
        }

        const isInView = el.offsetTop >= top && el.offsetHeight + el.offsetTop <= bottom;
        if (!isInView) {
            el.scrollIntoView({ block: "center" });
        }
    }, [props.mediaId, videoSidebarRef.current?.offsetHeight]);

    return (
        <div className="position-relative video-sidebar" ref={videoSidebarRef}>
            <div className="review-video-list-inner-container">
                <ConditionalSpinner isLoading={!hasLoadedMediaSourcesInitial}>
                    <>
                        <div className="review-video-list-search-container">
                            <Form.Control
                                type="search"
                                placeholder="Search videos..."
                                className="search-box"
                                value={searchText}
                                onChange={(e) => {
                                    setSearchText(e.currentTarget.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        setAppliedSearchText(searchText.trim());
                                    }
                                }}
                            />
                            <Button
                                variant="outline-secondary"
                                className="search-button"
                                title="Search"
                                onClick={() => {
                                    setAppliedSearchText(searchText.trim());
                                }}
                            >
                                <FontAwesomeIcon
                                    style={{ marginBottom: "1px", marginLeft: "-1px", marginRight: "1px" }}
                                    icon={faMagnifyingGlass}
                                />
                            </Button>
                        </div>
                        <div className="review-video-list-controls-container">
                            <Form.Select
                                aria-label="Sort media sources"
                                className="sort-by-select"
                                value={sortBy}
                                onChange={(e) => {
                                    setSortBy(e.currentTarget.value as MediaSourceSortBy);
                                }}
                            >
                                <option value="importTime">Import Order</option>
                                <option value="name">Name</option>
                                <option value="startTime">Start Time</option>
                                <option value="endTime">End Time</option>
                            </Form.Select>
                            <Button
                                variant="outline-secondary"
                                className="sort-direction-button"
                                title={isSortAscending ? "Sort descending" : "Sort ascending"}
                                onClick={() => {
                                    setIsSortAscending((prev) => !prev);
                                }}
                            >
                                <FontAwesomeIcon icon={isSortAscending ? faArrowUpShortWide : faArrowDownShortWide} />
                            </Button>
                            {failedImportsCount > 0 && (
                                <Button
                                    variant="outline-secondary"
                                    className="failed-visibility-button d-flex align-items-center gap-1"
                                    aria-label={`${
                                        showFailedImports ? "Hide failed imports" : "Show failed imports"
                                    } (${failedImportsCount})`}
                                    title={`${
                                        showFailedImports ? "Hide failed imports" : "Show failed imports"
                                    } (${failedImportsCount})`}
                                    onClick={() => {
                                        setShowFailedImports((prev) => !prev);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCircleExclamation} />
                                    <span style={{ marginTop: "-1px" }}>{failedImportsCount}</span>
                                </Button>
                            )}
                        </div>
                        <ListGroup variant="flush" className="gap-2 d-flex flex-column" ref={listGroupRef}>
                            {displayMediaSources.map((data) => {
                                const thumbnail = thumbnails?.thumbnails?.[data.mediaId];
                                const task = activeImportTaskByMediaId.get(data.mediaId);
                                return (
                                    <ReviewVideoListItem
                                        key={data.mediaId}
                                        data={data}
                                        viewId={props.viewId}
                                        activeMediaId={props.mediaId}
                                        setActiveMediaId={props.setMediaId}
                                        thumbnail={thumbnail}
                                        hasLoadedThumbnails={hasLoadedThumbnails}
                                        task={task}
                                    />
                                );
                            })}
                        </ListGroup>
                    </>
                </ConditionalSpinner>
            </div>
        </div>
    );
}

export default ReviewVideoList;
